import { isDev } from '@legacy/utils/env';
import { capitalize } from '@legacy/utils/string';
import type { User } from 'oidc-client-ts';
import type { PostHog } from 'posthog-js';

import { config } from '@/config';

/**
 * Identifies the authenticated user in PostHog so feature flags, surveys and
 * person properties resolve for the right `distinct_id`. Shared by the OIDC
 * signin callback and the dev test-login flow — both are "login" moments that
 * need to establish the PostHog identity once (PostHog then persists it across
 * reloads).
 */
export const identifyPostHogUser = (
  posthog: PostHog | undefined,
  user: User,
  currentLang: string,
): void => {
  // Reset PostHog to clear any previous user data
  posthog?.reset();

  const isProd = capitalize(config.environment.toLowerCase()) === 'Production';
  const org = (user.profile.sub ?? '').split(':')[0];
  const environment = isDev
    ? 'Development'
    : capitalize(config.environment.toLowerCase());
  const distinctUserId = isProd
    ? user.profile.sub
    : `${environment}:${user.profile.sub}`;

  // Identify sends an event, so you want may want to limit how often you call it
  posthog?.identify(distinctUserId, {
    organization: org,
    environment,
    // PostHog need language to be sent as `language` to work properly with targeting surveys
    // We also send it as `userLanguage` as we used to do, in case there are any existing dashboards relying on that property
    userLanguage: currentLang,
    language: currentLang,
  });

  if (isProd) {
    const customerProductId = (user.profile.sub ?? '').split(':')[2];
    posthog?.group('organization', org, {
      customerProductId: customerProductId,
    });
  }
};
